import React, {useEffect, useState} from 'react';
import "./index.css";
import { PaymentConfigurationObject, PaymentConfigurationProps } from "../../../interfaces";
import {
    Button,
    Container,
    Header,
    SpaceBetween,
    Input,
    Form,
    FormField,
    StatusIndicator,
    FlashbarProps
} from "@amzn/awsui-components-react-v3";
import { AxiosError, AxiosResponse } from "axios";
import { placePostRequest } from "../../../utils";
import constants from "../../../constants/strings";
import { isFieldValid } from "../../../utils/errorChecks";
import {getErrorFlashMessageProps} from "../../../utils/errorMessage";
import MatrixFlashMessage from "../CommonComponents/MatrixFlashMessage";
import {DEFAULT_EMPTY_FLASH_MESSAGE, UPDATE_SUCCESS_FLASH_MESSAGE} from "../../../constants/objects";
import MatrixSaveModal from "../CommonComponents/MatrixSaveModal";


const NUMERIC_ONLY_REGEX = /^\d+$/;

function PaymentConfiguration(props:PaymentConfigurationProps) {
    const [currency, setCurrency] = useState<string>(props.paymentData.currency);
    const [ofaOrg, setOfaOrg] = useState<string>(props.paymentData.ofaOrg);
    const [payGroup, setPayGroup] = useState<string>(props.paymentData.payGroup);
    const [headerDesc, setHeaderDesc] = useState<string>(props.paymentData.headerDesc);
    const [payeeSiteId, setPayeeSiteId] = useState<string>(props.paymentData.payeeSiteId);
    const [confirmPayeeSiteId, setConfirmPayeeSiteId] = useState<string>(props.paymentData.payeeSiteId);
    const [paymentDataUpdating, setPaymentDataUpdating] = useState<boolean>(false);
    const [currencyError, setCurrencyError] = useState<string>(constants.EMPTY_STRING);
    const [ofaOrgError, setOfaOrgError] = useState<string>(constants.EMPTY_STRING);
    const [payGroupError, setPayGroupError] = useState<string>(constants.EMPTY_STRING);
    const [payeeSiteIdError, setPayeeSiteIdError] = useState<string>(constants.EMPTY_STRING);
    const [confirmPayeeSiteIdError, setConfirmPayeeSiteIdError] = useState<string>(constants.EMPTY_STRING);
    const [message, setMessage] = useState<FlashbarProps.MessageDefinition>(DEFAULT_EMPTY_FLASH_MESSAGE);

    const [dismissedWarningIds, setDismissedWarningIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        setDismissedWarningIds(new Set());
    }, [props.paymentWarningMessages]);

    const warningFlashMessages: FlashbarProps.MessageDefinition[] = props.paymentWarningMessages.length > 0
        ? [
            ...props.paymentWarningMessages.map((msg, index) => ({
                type: "warning" as FlashbarProps.Type,
                content: msg,
                id: `warning-${index}`,
                dismissible: true,
                dismissLabel: "Dismiss message",
                onDismiss: () => setDismissedWarningIds(prev => new Set(prev).add(`warning-${index}`)),
            })),
            {
                type: "info" as FlashbarProps.Type,
                content: "The payment configuration between SPS and UPay is currently out of sync. Please review the payment configuration and save again to resolve.",
                id: "warning-info",
                dismissible: true,
                dismissLabel: "Dismiss message",
                onDismiss: () => setDismissedWarningIds(prev => new Set(prev).add("warning-info")),
            }
        ].filter(msg => !dismissedWarningIds.has(msg.id!))
        : [];

    function setStateToProps() {
        setCurrency(props.paymentData.currency);
        setOfaOrg(props.paymentData.ofaOrg);
        setPayGroup(props.paymentData.payGroup);
        setHeaderDesc(props.paymentData.headerDesc);
        setPayeeSiteId(props.paymentData.payeeSiteId);
        setConfirmPayeeSiteId(props.paymentData.payeeSiteId);
        setCurrencyError(constants.EMPTY_STRING);
        setOfaOrgError(constants.EMPTY_STRING);
        setPayGroupError(constants.EMPTY_STRING);
        setPayeeSiteIdError(constants.EMPTY_STRING);
        setConfirmPayeeSiteIdError(constants.EMPTY_STRING);
        setMessage(DEFAULT_EMPTY_FLASH_MESSAGE);
    }

    useEffect(() => {
        setStateToProps();
    }, [props.paymentData]);

    function updatePaymentConfigClicked() {

        // If values are empty set alert and do not make a call.
        if (!isFieldValid(currency, constants.CURRENCY_ERROR_TEXT, setCurrencyError)
            || !isFieldValid(ofaOrg, constants.OFA_ORG_ERROR_TEXT, setOfaOrgError)
            || !isFieldValid(payGroup, constants.PAY_GROUP_ERROR_TEXT, setPayGroupError)
            || !isFieldValid(payeeSiteId, constants.PAYEE_SITE_ID_ERROR_TEXT, setPayeeSiteIdError)) {
            return;
        }

        if (!NUMERIC_ONLY_REGEX.test(payeeSiteId)) {
            setPayeeSiteIdError(constants.PAYEE_SITE_ID_NUMERIC_ERROR_TEXT);
            return;
        }

        // Dual-entry verification: confirm field must always match payeeSiteId
        if (payeeSiteId.length > 0) {
            if (payeeSiteId !== confirmPayeeSiteId) {
                setConfirmPayeeSiteIdError(constants.PAYEE_SITE_ID_MISMATCH_ERROR_TEXT);
                return;
            }
        }

        setPaymentDataUpdating(true);
        const updatedPaymentConfiguration: PaymentConfigurationObject = {
            id: props.paymentData.id,
            currency: currency,
            ofaOrg: ofaOrg,
            payGroup: payGroup,
            headerDesc: headerDesc,
            payeeSiteId: payeeSiteId,
            spsVersionId: props.paymentData.spsVersionId
        }

        const data = {
            "operation": constants.SAVE_PAYMENT_CONFIGURATION,
            "request_body": {
                "scac": props.scac,
                "shipmentAccountType": props.accountType,
                "paymentConfiguration": {
                    "currencyCode": currency,
                    "region": ofaOrg,
                    "payGroup": payGroup,
                    "headerDesc": headerDesc,
                    "payeeSiteId": payeeSiteId,
                    "spsVersionId": props.paymentData.spsVersionId
                }
            }
        }
        const onSuccess = (res: AxiosResponse) => {
            props.updatePaymentConfiguration(updatedPaymentConfiguration);
            setPaymentDataUpdating(false);
            setMessage(UPDATE_SUCCESS_FLASH_MESSAGE);
        }
        const onError = (err: AxiosError) => {
            setPaymentDataUpdating(false);
            setMessage(getErrorFlashMessageProps(err));
        }
        placePostRequest(data, onSuccess, onError);
    }

    function handlePayeeSiteIdChange({ detail }: { detail: { value: string } }) {
        setPayeeSiteId(detail.value);
        setConfirmPayeeSiteId(constants.EMPTY_STRING);
        setConfirmPayeeSiteIdError(constants.EMPTY_STRING);
        if (detail.value.length === 0) {
            setPayeeSiteIdError(constants.PAYEE_SITE_ID_ERROR_TEXT);
        } else if (!NUMERIC_ONLY_REGEX.test(detail.value)) {
            setPayeeSiteIdError(constants.PAYEE_SITE_ID_NUMERIC_ERROR_TEXT);
        } else {
            setPayeeSiteIdError(constants.EMPTY_STRING);
        }
    }

    function handleConfirmPayeeSiteIdChange({ detail: inputChangeDetail }: { detail: { value: string } }) {
        setConfirmPayeeSiteId(inputChangeDetail.value);
        if (inputChangeDetail.value.length === 0) {
            setConfirmPayeeSiteIdError(constants.CONFIRM_PAYEE_SITE_ID_ERROR_TEXT);
        } else if (inputChangeDetail.value !== payeeSiteId) {
            setConfirmPayeeSiteIdError(constants.PAYEE_SITE_ID_MISMATCH_ERROR_TEXT);
        } else {
            setConfirmPayeeSiteIdError(constants.EMPTY_STRING);
        }
    }

    function isSaveDisabled(): boolean {
        return payeeSiteId.length > 0 && payeeSiteId !== confirmPayeeSiteId;
    }

    return (
        <div>
            <Container header={
                <Header variant="h2">
                    {constants.PAYMENT_CONFIGURATION}
                </Header>
            }>
            {
                props.paymentDataLoading ?
                    <StatusIndicator type="loading">{constants.LOADING}</StatusIndicator>
                    :
                    <div>
                        {
                            <SpaceBetween size={"l"}>
                                {warningFlashMessages.length > 0 && <MatrixFlashMessage content={warningFlashMessages} />}
                                <MatrixFlashMessage content={[message]} />
                                <Form
                                    actions={
                                        <SpaceBetween direction="horizontal" size="xs">
                                            <Button onClick={setStateToProps} variant="normal">{constants.RESET}</Button>
                                            {isSaveDisabled() ? (
                                                <Button variant="primary" disabled={true}>{constants.SAVE}</Button>
                                            ) : (
                                                <MatrixSaveModal buttonName={constants.SAVE} buttonLoading={paymentDataUpdating} onClickConfirm={updatePaymentConfigClicked}/>
                                            )}
                                        </SpaceBetween>
                                    }
                                >
                                    <SpaceBetween size={"l"} direction={"vertical"}>
                                        <FormField
                                            errorText={payeeSiteIdError}
                                            label={constants.PAYEE_SITE_ID}
                                        >
                                            <div
                                                onCopy={(e) => {
                                                    e.preventDefault();
                                                    e.clipboardData.setData("text/plain", payeeSiteId);
                                                }}
                                                onCut={(e) => {
                                                    e.preventDefault();
                                                    e.clipboardData.setData("text/plain", payeeSiteId);
                                                    setPayeeSiteId(constants.EMPTY_STRING);
                                                    setConfirmPayeeSiteId(constants.EMPTY_STRING);
                                                }}
                                            >
                                                <Input
                                                    onChange={handlePayeeSiteIdChange}
                                                    value={payeeSiteId}
                                                    type="text"
                                                    className="masked-input"
                                                />
                                            </div>
                                        </FormField>
                                        {payeeSiteId.length > 0 && (
                                            <FormField
                                                errorText={confirmPayeeSiteIdError}
                                                label={constants.CONFIRM_PAYEE_SITE_ID}
                                            >
                                                <div
                                                    onCut={(e) => e.preventDefault()}
                                                    onCopy={(e) => e.preventDefault()}
                                                    onPaste={(e) => e.preventDefault()}
                                                >
                                                    <Input
                                                        onChange={handleConfirmPayeeSiteIdChange}
                                                        value={confirmPayeeSiteId}
                                                    />
                                                </div>
                                            </FormField>
                                        )}
                                        <FormField
                                            errorText={currencyError}
                                            label={constants.CURRENCY}>
                                            <Input
                                                onChange={({ detail }) => {
                                                    setCurrency(detail.value);
                                                    if (detail.value.length === 0) {
                                                        setCurrencyError("Payment Currency Code is Required");
                                                    } else {
                                                        setCurrencyError(constants.EMPTY_STRING);
                                                    }
                                                }}
                                                onBlur={() => setCurrency(currency.trim())}
                                                value={currency}
                                            />
                                        </FormField>
                                        <FormField
                                            errorText={ofaOrgError}
                                            label={constants.OFA_ORG}>
                                            <Input
                                                onChange={({ detail }) => {
                                                    setOfaOrg(detail.value);
                                                    if (detail.value.length === 0) {
                                                        setOfaOrgError("Region is Required");
                                                    } else {
                                                        setOfaOrgError(constants.EMPTY_STRING);
                                                    }
                                                }}
                                                onBlur={() => setOfaOrg(ofaOrg.trim())}
                                                value={ofaOrg}
                                            />
                                        </FormField>
                                        <FormField
                                            errorText={payGroupError}
                                            label={constants.PAY_GROUP}>
                                            <Input
                                                onChange={({ detail }) => {
                                                    setPayGroup(detail.value);
                                                    if (detail.value.length === 0) {
                                                        setPayGroupError("Pay Group is Required");
                                                    } else {
                                                        setPayGroupError(constants.EMPTY_STRING);
                                                    }
                                                }}
                                                onBlur={() => setPayGroup(payGroup.trim())}
                                                value={payGroup}
                                            />
                                        </FormField>
                                        <FormField label={constants.HEADER_DESCRIPTION}>
                                            <Input
                                                onChange={({ detail }) => {
                                                    setHeaderDesc(detail.value);
                                                }}
                                                onBlur={() => setHeaderDesc(headerDesc.trim())}
                                                value={headerDesc}
                                            />
                                        </FormField>
                                    </SpaceBetween>
                                </Form>
                            </SpaceBetween>
                        }
                    </div>
            }
            </Container>
        </div>
    )
}

export default PaymentConfiguration;
